import { type Edge, type Node } from '@xyflow/react';
import { toast } from 'react-toastify';
import { type IVlanItem, type ProjectDataType } from 'src/context/global/types';
import { EDataType } from 'src/modules/network-configuration/models/node-data';

import { sortPortsROQSTAR } from './export-helpers';
import {
  exportToCSV,
  flattenDevicesForCSV,
  flattenSwitchesForCSV,
} from './export/csv';
import { buildCableTypeExcelBlob } from './export/excel-cable-type';
import {
  exportDevicesToExcel,
  buildDevicesExcelBlob,
} from './export/excel-devices';
import {
  exportPortAssignmentsToExcel,
  buildPortAssignmentsExcelBlob,
} from './export/excel-port-assignments';
import {
  exportSwitchesToExcel,
  buildSwitchesExcelBlob,
} from './export/excel-switches';
import {
  extractNodeData,
  extractCableTypeData,
  extractDevicesExportData,
  extractPortAssignmentData,
} from './export/extract';
import { exportDevicesToLibreOffice } from './export/ods-devices';
import { exportPortAssignmentsToLibreOffice } from './export/ods-port-assignments';
import { exportSwitchesToLibreOffice } from './export/ods-switches';
import { buildCableTypePdfBlob } from './export/pdf-cable-type';
import { buildDevicesPdfBlob } from './export/pdf-devices';
import { buildPortAssignmentsPdfBlob } from './export/pdf-port-assignments';
import { buildSwitchesPdfBlob } from './export/pdf-switches';
import {
  ExportDataType,
  ExportFileType,
  type NodeExportData,
  EXPORT_REPORT_TITLES,
  type ExportHeaderInfo,
  type CableTypeExportRow,
  type PortAssignmentExportData,
} from './export/types';

export { ExportDataType, ExportFileType };
export type { NodeExportData, CableTypeExportRow, PortAssignmentExportData };
export {
  exportToCSV,
  extractNodeData,
  extractCableTypeData,
  extractDevicesExportData,
  extractPortAssignmentData,
};

export const exportData = async (
  dataType: ExportDataType,
  fileType: ExportFileType,
  nodes: Node[],
  edges: Edge[],
  _vlans: IVlanItem[],
  projectData: ProjectDataType,
) => {
  const headerInfo: ExportHeaderInfo = {
    projectName: projectData?.projectName ?? '',
    companyName: projectData?.companyName,
    companyAddress: projectData?.companyAddress,
    drawnBy: projectData?.drawnBy,
    revisionNumber: projectData?.revisionNumber,
    projectNumber: projectData?.projectNumber,
    responsibleEngineer: projectData?.responsibleEngineer,
    description: projectData?.description,
    reportTitle: EXPORT_REPORT_TITLES[dataType],
  };

  switch (dataType) {
    case ExportDataType.SWITCHES: {
      const switchesData = extractNodeData(nodes, edges, EDataType.Switch);
      if (switchesData.length === 0) {
        toast.error('No data available for export');
        return;
      }
      if (fileType === ExportFileType.CSV) {
        exportToCSV(
          flattenSwitchesForCSV(switchesData),
          `${projectData?.projectName}_Switches`,
          headerInfo,
        );
      } else if (fileType === ExportFileType.EXCEL) {
        await exportSwitchesToExcel(
          switchesData,
          `${projectData?.projectName}_Switches`,
          headerInfo,
        );
      } else if (fileType === ExportFileType.LIBREOFFICE) {
        await exportSwitchesToLibreOffice(
          switchesData,
          `${projectData?.projectName}_Switches`,
          headerInfo,
        );
      }
      break;
    }
    case ExportDataType.DEVICE_IP_VLAN: {
      const devicesData = extractDevicesExportData(nodes, edges);
      if (devicesData.length === 0) {
        toast.error('No data available for export');
        return;
      }
      if (fileType === ExportFileType.CSV) {
        exportToCSV(
          flattenDevicesForCSV(devicesData),
          `${projectData?.projectName}_Devices`,
          headerInfo,
        );
      } else if (fileType === ExportFileType.EXCEL) {
        await exportDevicesToExcel(
          devicesData,
          `${projectData?.projectName}_Devices`,
          headerInfo,
        );
      } else if (fileType === ExportFileType.LIBREOFFICE) {
        await exportDevicesToLibreOffice(
          devicesData,
          `${projectData?.projectName}_Devices`,
          headerInfo,
        );
      }
      break;
    }
    case ExportDataType.PORT_ASSIGNMENT: {
      const portAssignmentsData = extractPortAssignmentData(nodes, edges);
      const sortedPortAssignmentsData = portAssignmentsData.sort(
        (a, b) =>
          a.Target.localeCompare(b.Target, undefined, { numeric: true }) ||
          sortPortsROQSTAR(a['Target Port'], b['Target Port']),
      );

      if (sortedPortAssignmentsData.length === 0) {
        toast.error('No data available for export');
        return;
      }
      if (fileType === ExportFileType.CSV) {
        exportToCSV(
          sortedPortAssignmentsData,
          `${projectData?.projectName}_PortAssignments`,
          headerInfo,
        );
      } else if (fileType === ExportFileType.EXCEL) {
        await exportPortAssignmentsToExcel(
          sortedPortAssignmentsData,
          `${projectData?.projectName}_PortAssignments`,
          headerInfo,
        );
      } else if (fileType === ExportFileType.LIBREOFFICE) {
        await exportPortAssignmentsToLibreOffice(
          sortedPortAssignmentsData,
          `${projectData?.projectName}_PortAssignments`,
          headerInfo,
        );
      }
      break;
    }
    case ExportDataType.CABLE_TYPE: {
      const cableTypeData = extractCableTypeData(edges, nodes);
      if (cableTypeData.length === 0) {
        toast.error('No data available for export');
        return;
      }
      if (fileType === ExportFileType.EXCEL) {
        const { blob, filename: fname } = await buildCableTypeExcelBlob(
          cableTypeData,
          `${projectData?.projectName}_CableType`,
          headerInfo,
        );
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fname;
        link.click();
      }
      break;
    }
    default:
      throw new Error(`Unknown data type: ${dataType}`);
  }
};

export const exportDataAsBlob = async (
  dataType: ExportDataType,
  format: 'excel' | 'pdf',
  nodes: Node[],
  edges: Edge[],
  _vlans: IVlanItem[],
  projectData: ProjectDataType,
): Promise<{ blob: Blob; filename: string } | null> => {
  const headerInfo: ExportHeaderInfo = {
    projectName: projectData?.projectName ?? '',
    companyName: projectData?.companyName,
    companyAddress: projectData?.companyAddress,
    drawnBy: projectData?.drawnBy,
    revisionNumber: projectData?.revisionNumber,
    projectNumber: projectData?.projectNumber,
    responsibleEngineer: projectData?.responsibleEngineer,
    description: projectData?.description,
    reportTitle: EXPORT_REPORT_TITLES[dataType],
  };

  switch (dataType) {
    case ExportDataType.SWITCHES: {
      const data = extractNodeData(nodes, edges, EDataType.Switch);
      if (data.length === 0) return null;
      return format === 'pdf'
        ? buildSwitchesPdfBlob(
            data,
            `${projectData.projectName}_Switches`,
            headerInfo,
          )
        : buildSwitchesExcelBlob(
            data,
            `${projectData.projectName}_Switches`,
            headerInfo,
          );
    }
    case ExportDataType.DEVICE_IP_VLAN: {
      const data = extractDevicesExportData(nodes, edges);
      if (data.length === 0) return null;
      return format === 'pdf'
        ? buildDevicesPdfBlob(
            data,
            `${projectData.projectName}_Devices`,
            headerInfo,
          )
        : buildDevicesExcelBlob(
            data,
            `${projectData.projectName}_Devices`,
            headerInfo,
          );
    }
    case ExportDataType.PORT_ASSIGNMENT: {
      const data = extractPortAssignmentData(nodes, edges);
      if (data.length === 0) return null;
      return format === 'pdf'
        ? buildPortAssignmentsPdfBlob(
            data,
            `${projectData.projectName}_PortAssignments`,
            headerInfo,
          )
        : buildPortAssignmentsExcelBlob(
            data,
            `${projectData.projectName}_PortAssignments`,
            headerInfo,
          );
    }
    case ExportDataType.CABLE_TYPE: {
      const data = extractCableTypeData(edges, nodes);
      if (data.length === 0) return null;
      return format === 'pdf'
        ? buildCableTypePdfBlob(
            data,
            `${projectData.projectName}_CableType`,
            headerInfo,
          )
        : buildCableTypeExcelBlob(
            data,
            `${projectData.projectName}_CableType`,
            headerInfo,
          );
    }
    default:
      return null;
  }
};
